import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { UIRouterGlobals } from "@uirouter/core";
import { StateService } from "@uirouter/core";
import { ChartLegendItem } from "chart.js";
import { merge } from "lodash";
import { forkJoin } from "rxjs";
import { finalize } from "rxjs/operators";
import { ChartHelperService } from "../chart/chart-helper.service";
import { TicksAgoService } from "../chart/ticks-ago.service";
import { DefaultChartResolutionService } from "../default-chart-resolution/default-chart-resolution.service";
import { EventService } from "../event-service/event.service";
import { AccountService } from "../models/account.service";
import { DeployService } from "../models/deploy.service";
import { DiffStatAggregationsService } from "../models/diff-stat-aggregations.service";
import { StatsmanService } from "../models/statsman.service";
import { formatNumberToHuman } from "../number-to-human/number-to-human";
import { RememberService } from "../remember/remember.service";
import { TimeDurAgoService } from "../time-dur-ago/time-dur-ago.service";
import { pluralize } from "../utils/pluralize";
import { sum } from "../utils/sum";

@Component({
  selector: "ab-project-detail",
  templateUrl: "./project-detail.component.html",
})
export class ProjectDetailComponent implements OnInit {
  @Input() public project: any;

  public loading: boolean;
  public dur: string;
  public lastN: string;
  public statsmanParams: any;
  public diffStatAggregations: any;
  public inited: boolean;
  public chartConfig: any;

  private REMEMBER_CHART_DURATION_KEY = "projects.chartDuration";
  private REMEMBER_CHART_LAST_N_KEY = "projects.chartLastN";

  constructor(
    private $state: StateService,
    private $uiRouterGlobals: UIRouterGlobals,
    private accountService: AccountService,
    private deployService: DeployService,
    private diffStatAggregationsService: DiffStatAggregationsService,
    private eventService: EventService,
    private rememberService: RememberService,
    private statsmanService: StatsmanService,
    private timeDurAgoService: TimeDurAgoService,
    private ticksAgoService: TicksAgoService,
    private chartHelperService: ChartHelperService,
    private changeDetector: ChangeDetectorRef,
    private defaultChartResolutionService: DefaultChartResolutionService,
  ) {}

  public ngOnInit() {
    this.eventService.publish("groups:activate", -1);

    this.loadChart({
      projectId: this.project.id,
      type: "counter",
      ...this.defaultChartResolution(),
    });
  }

  public loadChart(params: any) {
    this.loading = true;

    if (params.dur) {
      this.dur = params.dur;
      this.rememberService.set(this.REMEMBER_CHART_DURATION_KEY, params.dur);
    }
    if (params.last_n) {
      this.lastN = params.last_n;
      this.rememberService.set(this.REMEMBER_CHART_LAST_N_KEY, params.last_n);
    }

    if (this.statsmanParams != null) {
      this.statsmanParams = Object.assign(this.statsmanParams, params);
    } else {
      this.statsmanParams = params;
    }

    const noticesKey = `projects.${this.project.id}.notices`;
    const rateLimitedKey = `projects.${this.project.id}.rate_limited`;
    const overQuotaKey = `projects.${this.project.id}.over_quota`;
    const rejectedKey = `projects.${this.project.id}.rejections`;
    this.statsmanParams.name = [
      noticesKey,
      rateLimitedKey,
      overQuotaKey,
      rejectedKey,
    ];

    const deploysParams = {
      projectId: this.project.id,
      limit: 100,
    };

    forkJoin([
      this.statsmanService.query(this.statsmanParams),
      this.deployService.query(deploysParams),
      this.accountService.properties(),
      this.diffStatAggregationsService.get({
        projectId: this.project.id,
        createdAfter: this.timeDurAgoService.timeFor(this.dur, this.lastN),
      }),
    ])
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((data: any) => {
        this.diffStatAggregations = data[3];

        const statsmanQuery = data[0];
        const deployQuery = data[1];

        const totalAccepted = sum(statsmanQuery[noticesKey].count);
        const totalRateLimited = sum(statsmanQuery[rateLimitedKey].count);
        const totalOverQuota = sum(statsmanQuery[overQuotaKey].count);
        const totalRejected = sum(statsmanQuery[rejectedKey].count);
        const ticks = this.ticksAgoService.ticksFor(
          this.dur,
          Number(this.lastN),
        );

        // Legend items without a title will be hidden.
        statsmanQuery[noticesKey].title = `${formatNumberToHuman(
          totalAccepted,
        )} accepted error${totalAccepted === 1 ? "" : "s"}`;
        if (totalRateLimited > 0) {
          statsmanQuery[rateLimitedKey].title = `${formatNumberToHuman(
            totalRateLimited,
          )} rate limited`;
        }
        if (totalOverQuota > 0) {
          statsmanQuery[overQuotaKey].title = `${formatNumberToHuman(
            totalOverQuota,
          )} exceeded monthly quota`;
        }
        if (totalRejected > 0) {
          statsmanQuery[rejectedKey].title = `${formatNumberToHuman(
            totalRejected,
          )} rejected`;
        }

        statsmanQuery[noticesKey].tooltip = "accepted";
        statsmanQuery[rateLimitedKey].tooltip = "rate limited";
        statsmanQuery[overQuotaKey].tooltip = "exceeded quota";
        statsmanQuery[rejectedKey].tooltip = "rejected";

        this.chartConfig = {
          data: {
            labels: ticks,
            datasets: [
              {
                order: 1,
                label: `Accepted ${pluralize(totalAccepted, "error")}`,
                backgroundColor: "#b8b8b8", // ab-grey-700
                borderColor: "#b8b8b8", // ab-grey-700
                data: statsmanQuery[noticesKey].count,
                fixedNumberCount: formatNumberToHuman(totalAccepted),
              },
              {
                order: 2,
                label: `Rate limited ${pluralize(totalRateLimited, "error")}`,
                backgroundColor: "#ff3100", // ab-red-400
                borderColor: "#ff3100", // ab-red-400
                data: statsmanQuery[rateLimitedKey].count,
                fixedNumberCount: formatNumberToHuman(totalRateLimited),
              },
              {
                order: 3,
                label: `Exceeded quota ${pluralize(totalOverQuota, "error")}`,
                backgroundColor: "#222222", // ab-grey-100
                borderColor: "#222222", // ab-grey-100
                data: statsmanQuery[overQuotaKey].count,
                fixedNumberCount: formatNumberToHuman(totalOverQuota),
              },
              {
                order: 4,
                label: `Rejected ${pluralize(totalRejected, "error")}`,
                backgroundColor: "#0882b7", // ab-blue-400
                borderColor: "#0882b7", // ab-blue-400
                data: statsmanQuery[rejectedKey].count,
                fixedNumberCount: formatNumberToHuman(totalRejected),
              },
            ].concat(
              this.chartHelperService.generateDeployAnnotationsDataset(
                deployQuery.deploys,
                ticks,
                this.dur,
              ) as any,
            ),
          },
          options: merge(
            {},
            this.chartHelperService.defaultChartWithDeployAnnotationsOptions(),
            {
              legend: {
                labels: {
                  generateLabels(chart: any) {
                    return chart.data.datasets.map((dataset: any) => {
                      return {
                        text: `${dataset.fixedNumberCount} ${dataset.label}`,
                        fillStyle: dataset.backgroundColor,
                        strokeStyle: dataset.borderColor,
                      };
                    });
                  },
                  filter(legendItem: ChartLegendItem, _chart: Chart) {
                    if (legendItem.text.includes("Deploys")) {
                      return false;
                    }
                    if (legendItem.text.startsWith("0")) {
                      return false;
                    }

                    return true;
                  },
                },
              },
              onClick: (_event: MouseEvent, activeElements: any[]) => {
                const el = activeElements[0];
                if (!el) {
                  return;
                }

                const dataset = el._chart.data.datasets[el._datasetIndex];

                if (dataset.deployAnnotationsDataset) {
                  const deploy = dataset.deploys[el._index];
                  this.$state.go(
                    "deploys-show",
                    { projectId: this.project.id, deployId: deploy.id },
                    { inherit: false },
                  );
                } else {
                  // goab only filters days and shorter time frames won't work.
                  if (this.dur !== "90d/1d") {
                    return;
                  }

                  const date = el._chart.data.labels[el._index];
                  const filterParams = {
                    projectId: this.project.id,
                    filters: {
                      resolved: "any",
                      on: Math.round(date.setUTCHours(0, 0, 0, 0) / 1000),
                    },
                  };

                  this.$state.go("project-errors", filterParams, {
                    inherit: false,
                    reload: true,
                  });
                }

                this.chartHelperService.removeTooltip();
              },
              onHover: (event: MouseEvent, activeElements: any[]) => {
                const canvas = event.target as HTMLCanvasElement;
                canvas.style.cursor = activeElements[0] ? "pointer" : "default";
              },
            },
          ),
        };

        this.changeDetector.detectChanges();

        if (this.inited == null) {
          this.inited = true;
        }
      });
  }

  private defaultChartResolution(): { dur: string; last_n: string } {
    const dur =
      this.$uiRouterGlobals.params.dur ||
      this.rememberService.get(this.REMEMBER_CHART_DURATION_KEY);
    const lastN =
      this.$uiRouterGlobals.params.last_n ||
      this.rememberService.get(this.REMEMBER_CHART_LAST_N_KEY);
    if (dur && lastN) {
      return { dur, last_n: lastN };
    }

    if (this.project.firstNoticeReceivedAt == null) {
      // If firstNoticeReceivedAt is not set, this is likely a new project.
      return { dur: "90h/1h", last_n: "24" };
    } else {
      return this.defaultChartResolutionService.forTimestamp(
        this.project.firstNoticeReceivedAt,
      );
    }
  }
}
